"use client";

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Modal from '@/components/ui/Modal';

interface FacilityRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FacilityRequestModal({ isOpen, onClose }: FacilityRequestModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    facility: '',
    email: '',
    phone: '',
    role: '',
    payRange: '',
    timeline: '',
    notes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/facility-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);

        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            facility: '',
            email: '',
            phone: '',
            role: '',
            payRange: '',
            timeline: '',
            notes: '',
          });
          onClose();
        }, 3000);
      } else {
        alert(result.message || 'Failed to submit request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setIsSubmitted(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isSubmitted ? '' : 'Request Candidates'}>
      {isSubmitted ? (
        <div className="py-8 px-4 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-[#4fc589]/20 to-[#3a4d46]/40 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#4fc589]/20">
            <Icon name="CheckCircleIcon" size={32} className="text-[#5dd39e]" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Request Received!</h3>
          <p className="text-base text-[#a8c4b8] leading-relaxed">
            We'll contact you within 4 business hours.
          </p>
        </div>
      ) : (
        <div className="pb-2">
          <div className="text-center mb-4">
            <p className="text-sm text-[#a8c4b8] leading-relaxed">
              Takes 3 minutes • We respond in 4 hours
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label htmlFor="name" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 placeholder:text-[#a8c4b8]/40 shadow-inner"
                  placeholder="John Smith"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="facility" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  Facility Name *
                </label>
                <input
                  type="text"
                  id="facility"
                  name="facility"
                  required
                  value={formData.facility}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 placeholder:text-[#a8c4b8]/40 shadow-inner"
                  placeholder="Memorial Hospital"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="email" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 placeholder:text-[#a8c4b8]/40 shadow-inner"
                  placeholder="john@hospital.com"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="phone" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 placeholder:text-[#a8c4b8]/40 shadow-inner"
                  placeholder="555-123-4567"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="role" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  Role Needed *
                </label>
                <input
                  type="text"
                  id="role"
                  name="role"
                  required
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 placeholder:text-[#a8c4b8]/40 shadow-inner"
                  placeholder="RN ICU, Rad Tech, NP, etc."
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="payRange" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  Pay Range *
                </label>
                <input
                  type="text"
                  id="payRange"
                  name="payRange"
                  required
                  value={formData.payRange}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 placeholder:text-[#a8c4b8]/40 shadow-inner"
                  placeholder="$75k-$95k"
                />
              </div>

              <div className="md:col-span-2 space-y-1">
                <label htmlFor="timeline" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                  Timeline *
                </label>
                <input
                  type="text"
                  id="timeline"
                  name="timeline"
                  required
                  value={formData.timeline}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 placeholder:text-[#a8c4b8]/40 shadow-inner"
                  placeholder="ASAP, 2-4 weeks, 1-2 months"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label htmlFor="notes" className="block text-xs font-bold text-white tracking-wide uppercase text-[#7ee3b5]">
                Additional Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2.5 bg-[#0a0f0d]/80 border-2 border-[#3a4d46]/50 text-white text-sm rounded-xl focus:ring-2 focus:ring-[#4fc589] focus:border-[#4fc589] hover:border-[#4fc589]/60 outline-none transition-all duration-200 resize-none placeholder:text-[#a8c4b8]/40 shadow-inner"
                placeholder="Schedule requirements, shift details, certifications needed, etc."
              />
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative w-full px-6 py-3 bg-gradient-to-r from-[#3a4d46] to-[#4d6359] text-white text-base font-bold rounded-xl transition-all duration-300 hover:shadow-[0_0_50px_rgba(93,211,158,0.6)] hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center overflow-hidden shadow-lg"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#4d6359] to-[#5dd39e] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="mr-2 animate-spin relative" />
                    <span className="relative">Submitting...</span>
                  </>
                ) : (
                  <>
                    <span className="relative">Submit Request</span>
                    <Icon name="ArrowRightIcon" size={20} className="ml-2 relative group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </Modal>
  );
}